/**
 * assistantThread.tsx — conversation state AND the in-flight request for the
 * Assistente CME rail.
 *
 * Neither can live inside the rail component. The AppShell renders the collapsed
 * pill and the expanded panel as two different elements in two different places
 * in the tree, so React unmounts one and mounts the other every time the rail is
 * toggled — and `usePublishActiveProject` unpublishes on every navigation away
 * from Audit, unmounting the panel again. State held in the component would be
 * wiped by both.
 *
 * That is not only a lost transcript. A `useMutation` created inside the panel
 * takes its `isPending` with it, so a remount produces a fresh mutation that
 * believes nothing is in flight — which defeats the one-question-at-a-time guard
 * and lets two answers land against the wrong questions. Holding the pending set
 * here, keyed by project, is what makes the guard hold across a remount.
 *
 * Deliberately in-memory only: a reload starts a fresh conversation. Persisting
 * a transcript is a storage decision (retention, tenancy, GDPR) that has not
 * been taken, and inventing one here would be worse than the honest reset.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { apiErrorMessage, useApi } from "./api";
import {
  askProjectAssistant,
  type AssistantContext,
  type AssistantProgress,
  type AssistantSource,
  type AssistantTurn,
} from "./tenderApi";

/**
 * How many prior question/answer EXCHANGES to replay. Counted in pairs, not in
 * turns, on purpose: `answeredTurns` returns a pair-aligned array, so slicing it
 * by an odd number of turns would cut an exchange in half and send an answer
 * with no question in front of it. Expressing the cap in pairs makes that
 * impossible to get wrong later.
 *
 * 3 pairs = 6 turns, matching the server's own HISTORY_TURNS cap, so the client
 * does not pay to send what the server will discard.
 */
const HISTORY_PAIRS = 3;

/** Matches `AssistantTurn.content`'s `max_length=4000` (api/schemas.py). The
 *  server rejects the whole request if any replayed turn exceeds it, so the
 *  client must not send what the server will refuse. */
const MAX_TURN_CHARS = 4000;

export interface AssistantMessage {
  role: "user" | "ai";
  content: string;
  /** Set on an assistant turn that reports a failure rather than an answer. */
  error?: boolean;
  /** What the answer was based on; absent on user turns and on errors. */
  context?: AssistantContext;
  /** `KBR-74` — the same evidence, as objects the reader can open. Absent on
   *  user turns, on errors, and against a server that predates the field. */
  sources?: AssistantSource[];
  /** `FE-3`. Whether the chips below are REFERENCES the answer made, or simply
   *  the pages that were read. Absent on an older server → `pages`. */
  citationMode?: "markers" | "pages";
}

interface ThreadStore {
  threads: Record<string, AssistantMessage[]>;
  pending: Record<string, boolean>;
  /** 29 Sep build round: what the answer being built has done so far, per
   *  project — the steps the rail shows while the reader waits. */
  progress: Record<string, AssistantProgress | undefined>;
  drafts: Record<string, string>;
  ask: (projectId: string, question: string) => void;
  setDraft: (projectId: string, draft: string) => void;
}

const AssistantThreadContext = createContext<ThreadStore>({
  threads: {},
  pending: {},
  progress: {},
  drafts: {},
  ask: () => {},
  setDraft: () => {},
});

/**
 * The turns worth replaying: complete question/answer exchanges only.
 *
 * Dropping just the failed AI turn is not enough — that leaves the question it
 * failed to answer sitting in the history with no reply, so the model receives
 * two consecutive user turns and reads the orphan as context it already
 * addressed. A question and its answer travel together or not at all.
 */
function answeredTurns(messages: AssistantMessage[]): AssistantMessage[] {
  const kept: AssistantMessage[] = [];
  for (let i = 0; i < messages.length; i++) {
    const turn = messages[i];
    if (turn.role !== "user") continue;
    const reply = messages[i + 1];
    if (reply && reply.role === "ai" && !reply.error) kept.push(turn, reply);
  }
  return kept;
}

export function AssistantThreadProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useApi();
  const [threads, setThreads] = useState<Record<string, AssistantMessage[]>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [progress, setProgress] = useState<Record<string, AssistantProgress | undefined>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Both guards read from refs, not from state. A `setState` updater runs LATER
  // than the code that dispatched it, so reading the transcript inside one would
  // hand the request an empty history every time; and two sends in the same tick
  // would both see the pre-update `pending` and both fire. The ref is the
  // synchronous source of truth, with state mirroring it for rendering.
  const inFlight = useRef<Set<string>>(new Set());
  const threadsRef = useRef<Record<string, AssistantMessage[]>>({});

  const append = useCallback((projectId: string, message: AssistantMessage): void => {
    const prev = threadsRef.current;
    threadsRef.current = { ...prev, [projectId]: [...(prev[projectId] ?? []), message] };
    setThreads(threadsRef.current);
  }, []);

  const ask = useCallback(
    (projectId: string, question: string): void => {
      if (inFlight.current.has(projectId)) return;
      inFlight.current.add(projectId);
      setPending((prev) => ({ ...prev, [projectId]: true }));

      // Read the history BEFORE appending the new question, so the turn being
      // asked is not replayed back to the model as part of its own context.
      const existing = threadsRef.current[projectId] ?? [];
      const history: AssistantTurn[] = answeredTurns(existing)
        .slice(-HISTORY_PAIRS * 2)
        .map((m) => ({
          role: m.role === "ai" ? "assistant" : "user",
          // Clipped to the server's own per-turn limit. Replaying an answer
          // verbatim meant that the FIRST answer longer than the cap made every
          // later question in that thread fail validation — permanently, with no
          // way for the user to recover short of reloading the page.
          content: m.content.slice(0, MAX_TURN_CHARS),
        }));
      append(projectId, { role: "user", content: question });
      setDrafts((prev) => ({ ...prev, [projectId]: "" }));

      const settle = (message: AssistantMessage): void => {
        inFlight.current.delete(projectId);
        setPending((prev) => ({ ...prev, [projectId]: false }));
        setProgress((prev) => ({ ...prev, [projectId]: undefined }));
        append(projectId, message);
      };

      // The fifth argument is the live transcript: every poll reports the steps
      // so far, kept per project so a rail reopened mid-answer still shows them.
      setProgress((prev) => ({ ...prev, [projectId]: undefined }));
      void askProjectAssistant(api, projectId, question, history, (snapshot) =>
        setProgress((prev) => ({ ...prev, [projectId]: snapshot })),
      ).then(
        (reply) =>
          settle({
            role: "ai",
            content: reply.answer,
            context: reply.context,
            sources: reply.sources,
            citationMode: reply.citationMode,
          }),
        (err: unknown) =>
          settle({
            role: "ai",
            content: `Non sono riuscito a rispondere: ${apiErrorMessage(err)}`,
            error: true,
          }),
      );
    },
    [api, append],
  );

  // The half-written question lives here for the same reason the transcript
  // does: collapsing the rail unmounts the panel, and losing a typed draft to an
  // action the UI presents as harmless is its own small betrayal.
  const setDraft = useCallback((projectId: string, draft: string): void => {
    setDrafts((prev) => ({ ...prev, [projectId]: draft }));
  }, []);

  const value = useMemo<ThreadStore>(
    () => ({ threads, pending, progress, drafts, ask, setDraft }),
    [threads, pending, progress, drafts, ask, setDraft],
  );
  return (
    <AssistantThreadContext.Provider value={value}>{children}</AssistantThreadContext.Provider>
  );
}

export interface AssistantThread {
  messages: AssistantMessage[];
  pending: boolean;
  /** The steps of the answer being built, while `pending`. */
  progress: AssistantProgress | undefined;
  draft: string;
  ask: (question: string) => void;
  setDraft: (draft: string) => void;
}

/** Shared empty array: a fresh `[]` per render would change identity every time
 *  and defeat memoisation downstream. */
const EMPTY: AssistantMessage[] = [];

/**
 * The conversation for one project. Separate projects keep separate threads and
 * separate in-flight flags, so switching project never shows another tender's
 * answers and never blocks on another tender's request.
 */
export function useAssistantThread(projectId: string): AssistantThread {
  const store = useContext(AssistantThreadContext);
  const { ask, setDraft } = store;
  const messages = store.threads[projectId] ?? EMPTY;
  const pending = store.pending[projectId] ?? false;
  const progress = store.progress[projectId];
  const draft = store.drafts[projectId] ?? "";
  return useMemo<AssistantThread>(
    () => ({
      messages,
      pending,
      progress,
      draft,
      ask: (question: string) => ask(projectId, question),
      setDraft: (value: string) => setDraft(projectId, value),
    }),
    [messages, pending, progress, draft, ask, setDraft, projectId],
  );
}
